/**
 * JointAngleGoalAnnotation — Joint angle goal creation/editing panel
 *
 * Coaches define a joint angle goal by selecting:
 *  1. The joint to measure (from JOINT_DEFINITIONS, backend-verified names)
 *  2. Whether to match the template angle or set an explicit target range
 *  3. How strict the check should be, in degrees they would accept
 *     (StrictnessControl — observability Phase 4; the old Flexible / Balanced /
 *     Strict presets are gone, see STANDARD_TOLERANCE_DEGREES)
 *  4. Importance (Low / Medium / High / Critical)
 *
 * The saved payload round-trips a `joint_angle_goal` field accepted by the
 * backend evaluator (joint_angle_goal_evaluation.py).
 *
 * Design follows AxisLineAnnotation.tsx patterns (the most structurally
 * similar existing panel).
 */

import { useState, useCallback, useEffect } from 'react';
import { cn } from '@/utils/cn';
import AnnotationPanel from './AnnotationPanel';
import type { Phase, JointAngleConfig } from '@/types/api';
import { JOINT_NAMES } from './jointNames';
import StrictnessControl from './StrictnessControl';
import { strictnessValueText } from '@/utils/toleranceReviewCopy';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The standard strictness for a joint-angle check: the same 15° the backend
 * model default uses (`JointAngleConfig.angle_tolerance_degrees`, sourced from
 * `scoring_config`, ledger HC-26).
 *
 * This replaces a Flexible / Balanced / Strict preset trio (25 / 15 / 8,
 * ledger HC-30) whose outer two numbers had no experiment behind them and
 * whose 'Balanced' default — silently equal to the model default — is why 29
 * of the 41 live checks had never had their strictness chosen at all (study
 * metric M8). The coach now types the degrees they would accept; a named band
 * comes back only when calibration mints one from their own takes.
 */
const STANDARD_TOLERANCE_DEGREES = 15;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface JointAngleGoalAnnotationProps {
  isExpanded: boolean;
  onToggle: () => void;
  panelNumber: number;
  totalPanels: number;

  jointAngleConfig?: JointAngleConfig;
  onJointAngleConfigChange: (config: JointAngleConfig | undefined) => void;

  phases?: Phase[];
  currentPhase?: Phase | null;

  onSave: (
    config: JointAngleConfig,
    label: string,
    notes: string,
    importance: number,
  ) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function JointAngleGoalAnnotation({
  isExpanded,
  onToggle,
  panelNumber,
  totalPanels,
  jointAngleConfig,
  onJointAngleConfigChange,
  phases: _phases,
  currentPhase: _currentPhase,
  onSave,
}: JointAngleGoalAnnotationProps) {
  // AnnotationPanel form fields
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [importance, setImportance] = useState(33); // Default: Medium

  // Goal-specific fields
  const [jointName, setJointName] = useState('');
  const [referenceType, setReferenceType] = useState<'template' | 'target_range'>('template');
  const [targetAngle, setTargetAngle] = useState<string>('90');
  const [toleranceDegrees, setToleranceDegrees] = useState<number>(
    STANDARD_TOLERANCE_DEGREES,
  );
  const [toleranceSource, setToleranceSource] = useState<'default' | 'coach' | null>(
    null,
  );

  // Panel state
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Sync external config into local state (for edit-mode hydration)
  useEffect(() => {
    if (jointAngleConfig) {
      setJointName(jointAngleConfig.joint_name ?? '');
      setReferenceType(jointAngleConfig.reference_type ?? 'template');
      if (jointAngleConfig.target_angle_degrees != null) {
        setTargetAngle(String(jointAngleConfig.target_angle_degrees));
      }
      // Hydrate the strictness and WHO CHOSE IT. A stored config from before
      // the tolerance review carries no `tolerance_source`; it stays null and
      // StrictnessControl reads it as the standard setting only when the value
      // still is the standard one (same rule as the backend's
      // resolve_tolerance_source).
      setToleranceDegrees(
        jointAngleConfig.angle_tolerance_degrees ?? STANDARD_TOLERANCE_DEGREES,
      );
      setToleranceSource(jointAngleConfig.tolerance_source ?? null);
    }
  }, [jointAngleConfig]);

  // Build a live config object whenever fields change
  const buildConfig = useCallback((): JointAngleConfig => {
    const parsedTarget = parseFloat(targetAngle);
    return {
      enabled: true,
      joint_name: jointName,
      reference_type: referenceType,
      target_angle_degrees:
        referenceType === 'target_range' && !Number.isNaN(parsedTarget)
          ? parsedTarget
          : null,
      min_angle_degrees: null,
      max_angle_degrees: null,
      angle_tolerance_degrees: toleranceDegrees,
      // Omitted (rather than written as null) while the coach has not
      // answered, so a config this panel builds is byte-identical to the
      // pre-review shape until they do.
      ...(toleranceSource ? { tolerance_source: toleranceSource } : {}),
      measure_stability: false,
    };
  }, [jointName, referenceType, targetAngle, toleranceDegrees, toleranceSource]);

  // Propagate config changes upward
  useEffect(() => {
    if (jointName) {
      onJointAngleConfigChange(buildConfig());
    } else {
      onJointAngleConfigChange(undefined);
    }
  }, [jointName, buildConfig, onJointAngleConfigChange]);

  const canSave =
    jointName.trim().length > 0 &&
    label.trim().length > 0 &&
    (referenceType === 'template' ||
      (referenceType === 'target_range' && !Number.isNaN(parseFloat(targetAngle))));

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      await onSave(buildConfig(), label, notes, importance);
      setIsSaved(true);
      // Reset form
      setJointName('');
      setLabel('');
      setNotes('');
      setImportance(33);
      setReferenceType('template');
      setTargetAngle('90');
      setToleranceDegrees(STANDARD_TOLERANCE_DEGREES);
      setToleranceSource(null);
    } finally {
      setIsSaving(false);
    }
  }, [canSave, buildConfig, label, notes, importance, onSave]);

  // Reset isSaved after animation
  useEffect(() => {
    if (isSaved) {
      const t = setTimeout(() => setIsSaved(false), 2000);
      return () => clearTimeout(t);
    }
  }, [isSaved]);

  // Icon: a simple angle arc glyph
  const PanelIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 20V4M4 12h8" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 12a6 6 0 0 1 -6 6"
        strokeDasharray="2 1"
      />
    </svg>
  );

  return (
    <AnnotationPanel
      title="Joint Angle"
      subtitle="Measure and constrain joint bend angles"
      icon={PanelIcon}
      accentColor="amber"
      panelNumber={panelNumber}
      totalPanels={totalPanels}
      isExpanded={isExpanded}
      onToggle={onToggle}
      label={label}
      onLabelChange={setLabel}
      notes={notes}
      onNotesChange={setNotes}
      importance={importance}
      onImportanceChange={setImportance}
      canSave={canSave}
      isSaving={isSaving}
      isSaved={isSaved}
      onSave={handleSave}
      saveButtonText="Save Joint Angle Goal"
    >
      <div
        className="space-y-4"
        data-testid="joint-angle-goal-panel"
      >
        {/* Joint selector */}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1.5">
            Joint to Measure
          </label>
          <select
            value={jointName}
            onChange={(e) => setJointName(e.target.value)}
            className={cn(
              'w-full px-3 py-2 text-sm rounded-lg border bg-dark-elevated-1 text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-amber-500/40',
              jointName
                ? 'border-amber-500/60'
                : 'border-border-subtle',
            )}
            aria-label="Select joint to measure"
            data-testid="joint-name-select"
          >
            <option value="" disabled>Select a joint…</option>
            <optgroup label="Functional (handedness-aware)">
              {JOINT_NAMES.filter((j) => j.group === 'Functional').map((j) => (
                <option key={j.value} value={j.value}>{j.label}</option>
              ))}
            </optgroup>
            <optgroup label="Anatomical (fixed side)">
              {JOINT_NAMES.filter((j) => j.group === 'Anatomical').map((j) => (
                <option key={j.value} value={j.value}>{j.label}</option>
              ))}
            </optgroup>
          </select>
          {!jointName && (
            <p className="mt-1 text-[10px] text-text-muted">
              Functional joints (Lead/Trail) adapt to athlete handedness at evaluation time.
            </p>
          )}
        </div>

        {/* Reference type */}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-1.5">
            Condition
          </label>
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => setReferenceType('template')}
              className={cn(
                'px-3 py-2 text-xs font-medium rounded-lg border transition-colors text-left',
                referenceType === 'template'
                  ? 'border-amber-500/60 bg-amber-500/15 text-amber-300'
                  : 'border-border-subtle bg-dark-elevated-1 text-text-secondary hover:bg-dark-elevated-2',
              )}
              data-testid="ref-type-template"
            >
              Match Reference
              <span className="block text-[10px] font-normal text-text-muted mt-0.5">
                Follow the coach video's angle
              </span>
            </button>
            <button
              type="button"
              onClick={() => setReferenceType('target_range')}
              className={cn(
                'px-3 py-2 text-xs font-medium rounded-lg border transition-colors text-left',
                referenceType === 'target_range'
                  ? 'border-amber-500/60 bg-amber-500/15 text-amber-300'
                  : 'border-border-subtle bg-dark-elevated-1 text-text-secondary hover:bg-dark-elevated-2',
              )}
              data-testid="ref-type-target-range"
            >
              Set Target
              <span className="block text-[10px] font-normal text-text-muted mt-0.5">
                Specify an exact angle
              </span>
            </button>
          </div>
        </div>

        {/* Target angle (only when target_range mode) */}
        {referenceType === 'target_range' && (
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              Target Angle
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={180}
                step={1}
                value={targetAngle}
                onChange={(e) => setTargetAngle(e.target.value)}
                className={cn(
                  'w-24 px-3 py-2 text-sm rounded-lg border bg-dark-elevated-1 text-text-primary',
                  'focus:outline-none focus:ring-2 focus:ring-amber-500/40',
                  'border-border-subtle',
                )}
                aria-label="Target angle in degrees"
                data-testid="target-angle-input"
              />
              <span className="text-sm text-text-secondary">degrees</span>
              <span className="text-[10px] text-text-muted">(0–180°)</span>
            </div>
          </div>
        )}

        {/* Strictness — the tolerance review prompt (observability Phase 4).
            Replaces the Flexible / Balanced / Strict preset trio; see
            STANDARD_TOLERANCE_DEGREES above for why presets are gone. */}
        <StrictnessControl
          label={label.trim() || 'this check'}
          degrees={toleranceDegrees}
          source={toleranceSource}
          standardDegrees={STANDARD_TOLERANCE_DEGREES}
          comparedTo={referenceType === 'template' ? 'demo' : 'target'}
          onChange={(degrees, source) => {
            setToleranceDegrees(degrees);
            setToleranceSource(source);
          }}
        />

        {/* Config summary (shown when joint is selected) */}
        {jointName && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[10px] text-text-secondary space-y-0.5">
            <p>
              <span className="font-medium text-amber-300">
                {JOINT_NAMES.find((j) => j.value === jointName)?.label ?? jointName}
              </span>{' '}
              {referenceType === 'template'
                ? '— will follow the coach reference angle'
                : `— target ${targetAngle}°`}
              {' · '}
              {strictnessValueText(
                toleranceDegrees,
                referenceType === 'template' ? 'demo' : 'target',
              )}
            </p>
          </div>
        )}
      </div>
    </AnnotationPanel>
  );
}
